import { t } from '@glory/i18n';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
} from 'react';

import { Popover, PopoverAnchor, PopoverContent } from '../components/popover.tsx';
import { Spinner } from '../components/spinner.tsx';
import { cn } from '../lib/cn.ts';
import { FieldBox, type FieldSize, inputClass, useFieldControl, useFieldState } from './field.tsx';
import { OptionRow } from './listbox.tsx';

export interface SuggestInputProps<T> {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  /** Подсказки по введённому тексту; `signal` отменяет устаревший запрос. */
  readonly loadSuggestions?: ((query: string, signal: AbortSignal) => Promise<readonly T[]>) | undefined;
  readonly getKey: (item: T) => string;
  readonly describe: (item: T) => { readonly label: ReactNode; readonly description?: ReactNode };
  /** Выбрали подсказку: поле само решает, что поставить в значение и что заполнить рядом. */
  readonly onPick: (item: T) => void;
  readonly minQueryLength?: number;
  readonly debounceMs?: number;
  readonly placeholder?: string | undefined;
  readonly inputMode?: 'text' | 'numeric';
  readonly size?: FieldSize | undefined;
  readonly disabled?: boolean | undefined;
  readonly readOnly?: boolean | undefined;
  readonly invalid?: boolean | undefined;
  readonly trailing?: ReactNode;
  readonly onBlur?: () => void;
  readonly autoFocus?: boolean | undefined;
  readonly 'aria-label'?: string | undefined;
}

/**
 * Текст с подсказками: печатаете сами или выбираете подсказку (↑↓, Enter, Esc). В отличие от
 * выбора из списка, значение — любой текст: подсказки помогают, но не обязательны.
 */
export function SuggestInput<T>({
  value,
  onValueChange,
  loadSuggestions,
  getKey,
  describe,
  onPick,
  minQueryLength = 2,
  debounceMs = 250,
  placeholder,
  inputMode = 'text',
  size,
  trailing,
  onBlur,
  autoFocus,
  'aria-label': ariaLabel,
  ...state
}: SuggestInputProps<T>) {
  const field = useFieldState();
  const { disabled: fieldDisabled, readOnly: fieldReadOnly, ...control } = useFieldControl();
  const disabled = state.disabled ?? fieldDisabled ?? false;
  const readOnly = state.readOnly ?? fieldReadOnly ?? false;
  const invalid = state.invalid ?? field?.invalid ?? false;
  const listId = useId();
  const anchor = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<readonly T[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(-1);
  // Запрос идёт только после ввода человеком, а не при подстановке значения из подсказки.
  const [query, setQuery] = useState<string | null>(null);
  const load = useEffectEvent((q: string, signal: AbortSignal) => loadSuggestions?.(q, signal));

  useEffect(() => {
    if (query === null || query.trim().length < minQueryLength || !loadSuggestions) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      void load(query.trim(), controller.signal)
        ?.then(
          (found) => {
            if (controller.signal.aborted) return;
            setItems(found);
            setFailed(false);
            setActive(found.length > 0 ? 0 : -1);
            setOpen(true);
          },
          () => {
            if (controller.signal.aborted) return;
            setItems([]);
            setFailed(true);
            setOpen(true);
          },
        )
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, debounceMs);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, minQueryLength, debounceMs, loadSuggestions]);

  const pick = (item: T) => {
    setQuery(null);
    setOpen(false);
    onPick(item);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || items.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i + step + items.length) % items.length);
    } else if (e.key === 'Enter' && active >= 0) {
      const item = items[active];
      if (item !== undefined) {
        e.preventDefault();
        pick(item);
      }
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
    }
  };

  const optionId = (index: number) => `${listId}-${String(index)}`;
  const showList = open && (items.length > 0 || failed);

  return (
    <Popover open={showList} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <FieldBox
          ref={anchor}
          size={size}
          disabled={disabled}
          readOnly={readOnly}
          invalid={invalid}
          trailing={
            loading || trailing ? (
              <>
                {loading ? <Spinner /> : null}
                {trailing}
              </>
            ) : undefined
          }
        >
          <input
            {...control}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showList}
            aria-controls={showList ? listId : undefined}
            aria-activedescendant={showList && active >= 0 ? optionId(active) : undefined}
            aria-label={ariaLabel}
            autoFocus={autoFocus}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            inputMode={inputMode}
            autoComplete="off"
            spellCheck={false}
            className={cn(inputClass, inputMode === 'numeric' && 'tabular-nums')}
            value={value}
            onChange={(e) => {
              onValueChange(e.target.value);
              setQuery(e.target.value);
              if (e.target.value.trim().length < minQueryLength) setOpen(false);
            }}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
          />
        </FieldBox>
      </PopoverAnchor>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-72 p-1"
        // Фокус остаётся в поле: человек продолжает печатать.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (e.target instanceof Node && anchor.current?.contains(e.target)) e.preventDefault();
        }}
      >
        {failed ? (
          <p className="px-2.5 py-2 text-small text-muted">{t('fields.suggestUnavailable')}</p>
        ) : (
          <div
            id={listId}
            role="listbox"
            aria-label={t('fields.suggestions')}
            className="max-h-72 overflow-y-auto"
          >
            {items.map((item, index) => {
              const view = describe(item);
              return (
                <div
                  key={getKey(item)}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === active}
                  className="cursor-default select-none"
                  onMouseMove={() => {
                    setActive(index);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onClick={() => {
                    pick(item);
                  }}
                >
                  <OptionRow
                    label={view.label}
                    description={view.description}
                    active={index === active}
                    selected={false}
                  />
                </div>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
